import {QueryParameters} from "../../../../common/queryParameters";
import {PageScrollbar} from "../../../../common/pageScrollbar";
import {Dictionary} from "../../../../page/elements/dictionary";
import {Deferred, Promises, schedule} from "../../../../common/utils/promises";
import {resolve} from "../../../../container";
import {type Filters, SearchFacade} from "../searchFacade";
import type {FilterToggleEvent} from "./filterValue";
import {customElement, query, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {EopSearchFilterBlock, Filter} from "./filterBlock";
import {EventBus} from "../../../../common/eventBus";
import {FILTER_TOGGLE_EVENT, SEARCH_TILES_IN_VIEWPORT_EVENT} from "../constants";
import {ManagingResources} from "../../../../common/lifetime";
import {installEscapeHandlerOn, onTabPressAfter} from "../../../../common/utils/events";
import {trackingButtonInteract} from "../../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../../common/elements";
import {EopErrorHandler} from "../../../../common/errorHandler";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {CLOSE_ICON} from "../../../../common/svgIcons";

const SEARCH_FILTER_QUERY_PARAMETER_PREFIX = "filter_";
const RESULT_COUNT_PLACEHOLDER = "[COUNT]";

class FilterBlock {
    public constructor(
        public category: string,
        public filters: Filter[],
        public label: string,
        public disabled: boolean
    ) {
    }
}

@customElement("eop-search-filter")
export class EopSearchFilter extends ManagingResources(UnLitElement) {

    @state()
    private resultsLabel: string;
    @state()
    private filterBlocks: FilterBlock[];
    @state()
    private isUpToDate: boolean = true;

    @query("dialog")
    private filterDialog: HTMLDialogElement;

    private dictionary: Dictionary;
    private totalCount: number;
    private countUpdater: Deferred<number>;

    public constructor(
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        private eventBus: EventBus = resolve(EventBus),
        private searchFacade: SearchFacade = resolve(SearchFacade),
        private promises: Promises = resolve(Promises),
        private queryParameters: QueryParameters = resolve(QueryParameters),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();

        this.filterBlocks = [];
        this.totalCount = 0;
        this.countUpdater = new Deferred();
    }

    public render(): TemplateResult {
        const resetLabel = this.dictionary.translate("CONTENT_SEARCH_RESET");
        const searchLabel = this.dictionary.translate("CONTENT_SEARCH_FILTER");
        const closeLabel = this.dictionary.translate("MSG_CLOSE_DIALOG");
        return html`
            <div class="search-filter">
                <button type="button" class="filter-button"
                        @click=${this.openFilterDialog}
                        ${trackingButtonInteract(searchLabel)}
                >${searchLabel}</button>
                <dialog closedby="none" class="filter-container">
                    <div class="filter-panel">
                        <div class="filter-top-bar">
                            <div class="filter-top-bar-left-area">
                                <a class="reset-button"
                                        ?disabled=${!this.isResettable()}
                                        @click=${this.reset}
                                        ${trackingButtonInteract(resetLabel)}
                                >${resetLabel}</a>
                            </div>
                            <button class="close-filter" aria-label=${closeLabel}
                                    @click=${this.closeFilter}
                                    ${trackingButtonInteract(closeLabel)}
                            >${unsafeSVG(CLOSE_ICON)}</button>
                        </div>
                        <div class="filter-blocks">
                            ${(this.renderFilterBlocks())}
                        </div>
                        <div class="filter-panel-footer">
                            <button type="button" class="search-button"
                                    ?disabled=${this.isUpToDate}
                                    @click=${this.triggerSearch}
                                    ${trackingButtonInteract(this.resultsLabel)}
                            >${this.resultsLabel}</button>
                            <eop-overlay-image-spinner class="spinner-sm" event="result-count"></eop-overlay-image-spinner>
                        </div>
                    </div>
                </dialog>
            </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.searchFacade.registerFilterInitialization(async (sf) => {
            this.queryParameters.onStringsStartingWith(SEARCH_FILTER_QUERY_PARAMETER_PREFIX, filters => {
                const initialFilters: Filters = new Map();

                filters.forEach((values, category) => {
                    initialFilters.set(category, Array.from(new Set(values ?? [])));
                });

                sf.addFilters(initialFilters);
            });
            await sf.fetchFilters();
            return sf;
        });
    }

    private renderFilterBlocks(): TemplateResult[] {
        return this.filterBlocks.map(block => {
            return html`
                <eop-search-filter-block
                        .category=${block.category}
                        .label=${this.dictionary.translate(block.label)}
                        .filters=${block.filters}
                        ?disabled=${block.disabled}
                ></eop-search-filter-block>
            `;
        });
    }

    public lastFocusableElement(): HTMLElement {
        const searchButton = this.renderRoot.querySelector<HTMLButtonElement>(".search-button");
        if (searchButton && !searchButton.disabled) {
            return searchButton;
        }
        const filterButtons = Array.from(this.renderRoot.querySelectorAll<EopSearchFilterBlock>("eop-search-filter-block"))
            .filter(block => !block.hasAttribute("disabled"))
            .flatMap(block => block.focusableElements());
        if (filterButtons.isNotEmpty()) {
            return filterButtons.last()!;
        }
        return this.renderRoot.querySelector<HTMLButtonElement>(".close-filter")!;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.searchFacade.onUpdateFilter((_, totalCount) => {
            this.updateCount(totalCount);
            this.updateFilterBlocks();
        });
        this.searchFacade.onAppliedFilter(filters => this.persistFilters(filters));

        this.searchFacade.onChangeGroup(() => {
            this.updateFilterBlocks();

            this.updateTotalCount().then(() => {
                this.resultsLabel = this.getShowResultsLabel();
            });
        });

        this.updateFilterBlocks();
        this.dictionary = Dictionary.of(this);
        this.resultsLabel = this.getShowResultsLabel();

        this.addEventListener(FILTER_TOGGLE_EVENT, (event: Event) => {
            const eventDetails = (event as FilterToggleEvent).detail;
            void this.toggleFilter(eventDetails.category, eventDetails.value);
        });
        this.eventBus.on(SEARCH_TILES_IN_VIEWPORT_EVENT, event => {
            this.classList.toggle("tiles-in-viewport", event.data.inViewport);
        }, this);

        installEscapeHandlerOn(this, () => this.closeFilter());
    }

    private updateFilterBlocks(): void {
        this.filterBlocks = this.getFilterBlocks();
    }

    private async openFilterDialog(): Promise<void> {
        this.openFilter();
        onTabPressAfter(() => this.lastFocusableElement(), () => this.closeFilter());
        this.resetToAppliedState();
        await this.updateTotalCount();
        this.updateProperties();
    }

    public async toggleFilter(category: string, value: string): Promise<void> {
        this.searchFacade.toggleFilter(category, value);
        await this.updateResultCount();
        this.updateProperties();
    }

    public async triggerSearch(): Promise<void> {
        await schedule(this.searchFacade.commitFilters()).as("trigger-search");
        this.closeFilter();
    }

    private openFilter(): void {
        this.filterDialog.showModal();
        this.pageScrollbar.disablePageScrollability();
    }

    private closeFilter(): void {
        this.filterDialog.close();
        this.pageScrollbar.enablePageScrollability();
    }

    public async reset(): Promise<void> {
        await schedule(this.internalReset()).as("filter-reset");
        this.updateProperties();
    }

    private async internalReset(): Promise<void> {
        await this.searchFacade.resetFilters();
        await this.updateResultCount();
    }

    private updateProperties(): void {
        this.filterBlocks = this.getFilterBlocks();
        this.resultsLabel = this.getShowResultsLabel();
        this.isUpToDate = !this.isChanged();
    }

    private getShowResultsLabel(): string {
        const totalCount = this.getTotalCount();
        const subkey = totalCount === 1 ? "1" : "_";
        return this.dictionary.translate("CONTENT_SEARCH_RESULTS_SHOW", subkey, {[RESULT_COUNT_PLACEHOLDER]: totalCount});
    }

    public getFilterBlocks(): FilterBlock[] {
        const allFilterData = this.searchFacade.getAllFilterData() ?? [];
        const selectedFilters = this.searchFacade.getSelectedFilters();
        const applicableFilterData = this.searchFacade.applicableFilterData();

        return allFilterData.map(data => {
            const filters = data.filters.map(filter => {
                const isActive = (selectedFilters.get(data.id) ?? []).includes(filter);
                const isDisabled = applicableFilterData.every(applicableData => !applicableData.filters.includes(filter));
                return new Filter(filter, isActive, isDisabled);
            });
            const isBlockDisabled = applicableFilterData.every(applicableData => applicableData.id !== data.id);
            return new FilterBlock(data.id, filters, data.label, isBlockDisabled);
        });
    }

    public getTotalCount(): number {
        return this.totalCount;
    }

    public async updateTotalCount(): Promise<void> {
        await this.updateResultCount();
    }

    public resetToAppliedState(): void {
        this.searchFacade.revertFilters();
    }

    public isResettable(): boolean {
        return this.isChanged() || this.searchFacade.getSelectedFilters().size > 0;
    }

    public isChanged(): boolean {
        return this.searchFacade.hasUncommittedFilters();
    }

    private updateCount(totalCount: number): void {
        this.totalCount = Math.max(totalCount, 0);
    }

    private async updateResultCount(): Promise<void> {
        const filters = this.searchFacade.getSelectedFilters();
        this.countUpdater.rejectExpected();
        const deferred = new Deferred<number>();
        this.countUpdater = deferred;

        void this.triggerGetActiveResultsCount(filters, deferred);
        const spinnerForResultCount = this.promises.decoratorFor("result-count")(this.fetchTotalCount(deferred));
        await schedule(spinnerForResultCount).as("update-result-count");
    }

    private async triggerGetActiveResultsCount(filters: Map<string, string[]>, deferred: Deferred<number>): Promise<void> {
        const resultsCount = await this.searchFacade.requestActiveResultsCount(filters);
        deferred.resolve(resultsCount);
    }

    private async fetchTotalCount(deferred: Deferred<number>): Promise<void> {
        try {
            this.totalCount = await deferred.promise;
        } catch (e) {
            this.errorHandler.report(e);
        }
    }

    private persistFilters(filters: Filters): void {
        this.queryParameters.removeParametersStartingWith(SEARCH_FILTER_QUERY_PARAMETER_PREFIX);
        filters.forEach((values, category) => {
            this.queryParameters.appendAll(category, values);
        });
    }
}